/**
 * @module app.um
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService, IAuthMedicationStatusHistorySearchFilters } from 'app/um/services/auth-service';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class AuthMedicationsStatusHistoryDetail extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected authId: string | null = null;

    @bindable
    protected medicationId: string | null = null;

    protected authMedicationStatusHistory: UM.AuthMedicationStatusHistory[] = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadAuthMedicationHistory();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadAuthMedicationHistory(): Promise<void> {
        if(!this.authId || !this.medicationId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: true
        };

        const includes = [
            'audit.start',
            'audit.start.user',
            'status',
            'status.reason'
        ];

        const fields = [
            'audit.start.timestamp',
            'audit.start.user.description',
            'status.description',
            'status.reason.description'
        ];

        const authMedicationStatusHistoryFilters: IAuthMedicationStatusHistorySearchFilters = { medicationId: this.medicationId };

        try {
            this.state.beginLoad();

            const response = await this.service.loadAuthMedicationStatusHistoryByAuth(this.authId, options, includes, fields, authMedicationStatusHistoryFilters);
            this.authMedicationStatusHistory = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
